import { instanceToPlain, plainToInstance } from 'class-transformer';
import { collection, doc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Condominium, Section, Unit } from '../../entities';
import { Collections, db, functions } from '../../utils/firebase.config';
import { converterOf } from '../../utils/firebase.utils';
import CreateUnitInput from './resources/create-unit.resource';
import UpdateUnitInput from './resources/update-unit.resource';
import ImportSectionsUnitsData from '../../entities/sections-units.entity';

export type UnitsWithSection = Omit<Unit, 'sectionId'> & { section?: Section };

export const useUnits = (condominiumId: string) => {
  const condominiumsCollection = collection(db, Collections.Condominiums).withConverter(converterOf(Condominium));
  const condominiumsDoc = doc(condominiumsCollection, condominiumId);

  const unitsCollection = collection(condominiumsDoc, Collections.Units).withConverter(converterOf(Unit));

  const fetchAll = async (): Promise<Unit[]> => {
    const response = await getDocs(unitsCollection);

    return response.docs.map((unit) => unit.data());
  };

  const create = async (input: Omit<Unit, 'id'>): Promise<Unit> => {
    const createUnit = httpsCallable<unknown, Unit>(functions, 'insertCondominiumUnit');

    const newUnit = new CreateUnitInput({ ...input, condominiumId });

    const { data } = await createUnit(instanceToPlain(newUnit));

    return plainToInstance(Unit, data);
  };

  const importDocument = async (url: string ): Promise<void> => {
      const importSectionsUnits =  httpsCallable<unknown, ImportSectionsUnitsData>(functions, 'insertCondominiumSectionUnit');

      const newSectionsUnits = new ImportSectionsUnitsData({ url, condominiumId });

      await importSectionsUnits(instanceToPlain(newSectionsUnits));
  };

  const update = async (input: Partial<Unit> & { id: string }): Promise<void> => {
    const updateUnit = httpsCallable(functions, 'updateCondominiumUnit');

    const unit = new UpdateUnitInput({ ...input, number: input.number?.toString(), condominiumId });
    await updateUnit(instanceToPlain(unit));
  };

  return { fetchAll, create, update, importDocument };
};

export default useUnits;

export const lazyFetchUnits = async (condominiumId: string): Promise<Unit[]> => {
  const condominiumsCollection = collection(db, Collections.Condominiums).withConverter(converterOf(Condominium));
  const condominiumsDoc = doc(condominiumsCollection, condominiumId);

  const unitsCollection = collection(condominiumsDoc, Collections.Units).withConverter(converterOf(Unit));

  const response = await getDocs(unitsCollection);

  return response.docs.map((unit) => unit.data());
};

export const lazyFetchUnitsWithSection = async (condominiumId: string): Promise<UnitsWithSection[]> => {
  const condominiumsCollection = collection(db, Collections.Condominiums).withConverter(converterOf(Condominium));
  const condominiumsDoc = doc(condominiumsCollection, condominiumId);

  const unitsCollection = collection(condominiumsDoc, Collections.Units).withConverter(converterOf(Unit));
  const sectionCollection = collection(condominiumsDoc, Collections.Sections).withConverter(converterOf(Section));

  const unitResponse = await getDocs(unitsCollection);
  const unitData = unitResponse.docs.map((unit) => unit.data());

  const sectionResponse = await getDocs(sectionCollection);
  const sectionData = sectionResponse.docs.map((section) => section.data());

  return unitData.map((unit) => {
    const result: UnitsWithSection = {
      ...unit,
      section: sectionData.find((section) => section.id === unit.sectionId),
    };
    return result;
  });
};
